import { formatPropertyUnitLabel } from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import type { WorkOrderOpenTarget } from "../../../../../../../../../../../../../api/kehrblatt/work-orders"
import { internalOrderTypeLabel } from "../../../../../../../../../../../../../_shared/_logic/order-type-labels"
import type { AddressOpenWorkOrderWarning } from "../../../_shared/_logic/batch-upload-contract"

export type OpenWorkOrderWarningMap = Record<string, AddressOpenWorkOrderWarning>

export type OpenWorkOrderWarningTagText = {
  label: string
  subLabel: string
}

export const buildOpenWorkOrderWarningTagText = (
  warning?: AddressOpenWorkOrderWarning | null,
): OpenWorkOrderWarningTagText =>
  warning
    ? { label: "Mehrfach vorhanden", subLabel: `${warning.count} offen` }
    : { label: "Nicht doppelt", subLabel: "keine offenen Aufträge" }

export const openWorkOrderTargetKey = ({
  propertyId,
  propertyUnitId,
}: {
  propertyId?: string | null
  propertyUnitId?: string | null
}): string | null => {
  const unitId = String(propertyUnitId ?? "").trim()

  if (unitId) return `unit:${unitId}`

  const targetPropertyId = String(propertyId ?? "").trim()

  return targetPropertyId ? `property:${targetPropertyId}` : null
}

const labelForWorkOrder = (order: WorkOrderOpenTarget): string => {
  const numberLabel = order.order_number ? `Auftrag ${order.order_number}` : "Auftrag"
  const orderType = internalOrderTypeLabel(order)
  const typeLabel = orderType ? ` · ${orderType}` : ""
  const unitLabel = order.property_unit_label
    ? ` · ${formatPropertyUnitLabel(order.property_unit_label)}`
    : ""

  return `${numberLabel}${typeLabel}${unitLabel}`
}

const appendWarning = (
  warnings: OpenWorkOrderWarningMap,
  key: string,
  label: string,
): OpenWorkOrderWarningMap => {
  const current = warnings[key] ?? { count: 0, labels: [] }
  const labels = current.labels.includes(label) ? current.labels : [...current.labels, label]

  return {
    ...warnings,
    [key]: {
      count: current.count + 1,
      labels,
    },
  }
}

export const buildOpenWorkOrderWarningMap = (
  orders: WorkOrderOpenTarget[],
): OpenWorkOrderWarningMap =>
  orders.reduce<OpenWorkOrderWarningMap>((warnings, order) => {
    const key = openWorkOrderTargetKey({
      propertyId: order.property_id,
      propertyUnitId: order.property_unit_id,
    })

    return key ? appendWarning(warnings, key, labelForWorkOrder(order)) : warnings
  }, {})

export const mergeOpenWorkOrderWarnings = (
  entries: Array<AddressOpenWorkOrderWarning | null | undefined>,
): AddressOpenWorkOrderWarning | null => {
  const labels = Array.from(new Set(entries.flatMap((entry) => entry?.labels ?? [])))
  const count = entries.reduce((total, entry) => total + (entry?.count ?? 0), 0)

  return count > 0 ? { count, labels } : null
}

export const openWorkOrderWarningForSelection = ({
  propertyId,
  selectedUnitIds,
  warningsByTarget,
}: {
  propertyId?: string | null
  selectedUnitIds: string[]
  warningsByTarget: OpenWorkOrderWarningMap
}): AddressOpenWorkOrderWarning | null => {
  const propertyWarning = propertyId ? warningsByTarget[`property:${propertyId}`] : null
  const unitWarnings = selectedUnitIds.map((unitId) => warningsByTarget[`unit:${unitId}`])

  return mergeOpenWorkOrderWarnings([propertyWarning, ...unitWarnings])
}
